/**
 * DirectivesEditor - Manage persistent optimization directives
 *
 * Directives are standing instructions for the optimizer that apply
 * to all future iterations until changed or removed.
 */

import React, { useState, useEffect } from 'react';
import { Plus, X, Save, Loader2, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getAuthHeaders } from '@/services/authConfig';

interface DirectivesEditorProps {
  evalId: string;
  disabled?: boolean;
}

const EXAMPLE_DIRECTIVES = [
  'Keep the prompt under 500 words',
  'Don\'t modify the tool list',
  'Focus on improving response structure',
  'Maintain a professional tone',
  'Preserve all code examples',
];

export function DirectivesEditor({ evalId, disabled = false }: DirectivesEditorProps) {
  const [directives, setDirectives] = useState<string[]>([]);
  const [newDirective, setNewDirective] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalDirectives, setOriginalDirectives] = useState<string[]>([]);

  // Fetch current directives
  useEffect(() => {
    const fetchDirectives = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/evals/${evalId}/optimization/directives`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setDirectives(data.directives || []);
          setOriginalDirectives(data.directives || []);
        }
      } catch (err) {
        console.error('Failed to fetch directives:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDirectives();
  }, [evalId]);

  // Track changes
  useEffect(() => {
    const changed = JSON.stringify(directives) !== JSON.stringify(originalDirectives);
    setHasChanges(changed);
  }, [directives, originalDirectives]);

  const handleAddDirective = () => {
    if (!newDirective.trim()) return;
    setDirectives([...directives, newDirective.trim()]);
    setNewDirective('');
  };

  const handleRemoveDirective = (index: number) => {
    setDirectives(directives.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/evals/${evalId}/optimization/directives`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ directives }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to save directives');
      }
      setOriginalDirectives([...directives]);
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDirective();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Optimizer Directives
        </CardTitle>
        <CardDescription>
          Standing instructions for the optimizer. These apply to all future iterations.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current directives */}
        {directives.length > 0 ? (
          <div className="space-y-2">
            {directives.map((directive, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-2 bg-muted/50 rounded-md"
              >
                <span className="flex-1 text-sm">{directive}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => handleRemoveDirective(index)}
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground py-2">
            No directives set. Add directives to guide the optimizer.
          </div>
        )}

        {/* Add new directive */}
        <div className="flex gap-2">
          <Input
            value={newDirective}
            onChange={(e) => setNewDirective(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a directive..."
            disabled={disabled}
            className="flex-1"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={handleAddDirective}
            disabled={disabled || !newDirective.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Example directives */}
        {directives.length === 0 && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Example directives:</p>
            <div className="flex flex-wrap gap-1">
              {EXAMPLE_DIRECTIVES.slice(0, 3).map((example, i) => (
                <Badge
                  key={i}
                  variant="outline"
                  className="cursor-pointer hover:bg-muted"
                  onClick={() => !disabled && setDirectives([...directives, example])}
                >
                  {example}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        {/* Save button */}
        {hasChanges && (
          <Button
            onClick={handleSave}
            disabled={saving || disabled}
            className="w-full"
            size="sm"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Directives
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default DirectivesEditor;
